import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

export const fetchAIChat = createAsyncThunk(
  "aiChat/fetchAIChat",
  async ({ group_id, userToken }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/chatembed/${group_id}`, {
        headers: { token: userToken }
      });
      console.log(data);
      const messages = data.messages.map(msg => ({
        ...msg,
        isAI: !!msg.agent,
        temp: false,
        contentReferences: msg.metadata?.contentReferences || null
      }));
      return { chat: data.aiChat, messages };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const sendMessage = createAsyncThunk(
  "aiChat/sendMessage",
  async ({ groupId, message, userToken }, { rejectWithValue, dispatch }) => {
    const tempId = `temp-${Date.now()}`;
    const streamId = `stream-${Date.now()}`;

    // Optimistic user message
    dispatch(
      addMessage({
        id: tempId,
        isAI: false,
        message,
        user: {},
        temp: true,
        createdAt: new Date().toISOString()
      })
    );

    try {
      const response = await fetch(`/api/chatembed/${groupId}/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          token: userToken
        },
        body: JSON.stringify({ message })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: "Request failed" }));
        throw new Error(err.message || `HTTP ${response.status}`);
      }

      // Read SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let streamingStarted = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          let event;
          try {
            event = JSON.parse(line.slice(6));
          } catch {
            continue;
          }

          switch (event.type) {
            case "status":
              dispatch(setStatusMessage(event.message));
              break;

            case "user_message":
              // Replace temp with confirmed user message
              dispatch(removeTempMessage(tempId));
              dispatch(
                addMessage({ ...event.userMessage, isAI: false, temp: false, skipAnimation: true })
              );
              break;

            case "content_references":
              dispatch(setPendingContentReferences(event.contentReferences || []));
              break;

            case "chunk":
              if (!streamingStarted) {
                streamingStarted = true;
                dispatch(setStatusMessage(null));
                // Add streaming AI message placeholder
                dispatch(
                  addMessage({
                    id: streamId,
                    isAI: true,
                    message: event.text,
                    temp: true,
                    streaming: true,
                    createdAt: new Date().toISOString()
                  })
                );
              } else {
                dispatch(appendToStreamingMessage({ id: streamId, text: event.text }));
              }
              break;

            case "done":
              // Replace streaming message with final DB-confirmed one
              dispatch(removeTempMessage(streamId));
              if (event.aiMessage) {
                dispatch(
                  addMessage({
                    ...event.aiMessage,
                    isAI: true,
                    temp: false,
                    skipAnimation: true,
                    contentReferences: event.aiMessage.metadata?.contentReferences || null
                  })
                );
              }
              dispatch(setStatusMessage(null));
              dispatch(setPendingContentReferences(null));
              break;

            case "error":
              throw new Error(event.message);
          }
        }
      }

      dispatch(setStatusMessage(null));
      return { success: true };
    } catch (err) {
      dispatch(removeTempMessage(tempId));
      dispatch(removeTempMessage(streamId));
      dispatch(setStatusMessage(null));
      dispatch(setPendingContentReferences(null));
      return rejectWithValue(err.message || "Something went wrong");
    }
  }
);

export const clearChatHistory = createAsyncThunk(
  "aiChat/clearChatHistory",
  async ({ groupId, userToken }, { rejectWithValue, dispatch }) => {
    try {
      await axios.delete(`/api/chatembed/${groupId}/messages`, {
        headers: { token: userToken }
      });
      dispatch(clearChat());
      return true;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

const aiChatSlice = createSlice({
  name: "aiChat",
  initialState: {
    chat: null,
    messages: [],
    loading: false,
    error: null,
    sending: false,
    sendError: null,
    statusMessage: null,
    pendingContentReferences: null
  },
  reducers: {
    addMessage: (state, { payload }) => {
      const existing = state.messages.find(m => m.id === payload.id);
      if (existing) Object.assign(existing, payload);
      else state.messages.push(payload);
      state.messages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    },
    appendToStreamingMessage: (state, { payload }) => {
      const msg = state.messages.find(m => m.id === payload.id);
      if (msg) msg.message += payload.text;
    },
    removeTempMessage: (state, { payload }) => {
      state.messages = state.messages.filter(m => m.id !== payload);
    },
    clearChat: state => {
      state.chat = null;
      state.messages = [];
    },
    setStatusMessage: (state, { payload }) => {
      state.statusMessage = payload;
    },
    setPendingContentReferences: (state, { payload }) => {
      state.pendingContentReferences = payload;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAIChat.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAIChat.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.chat = payload.chat;
        state.messages = payload.messages;
      })
      .addCase(fetchAIChat.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      .addCase(sendMessage.pending, state => {
        state.sending = true;
        state.sendError = null;
      })
      .addCase(sendMessage.fulfilled, state => {
        state.sending = false;
        state.sendError = null;
      })
      .addCase(sendMessage.rejected, (state, { payload }) => {
        state.sending = false;
        state.sendError = payload;
      });
  }
});

export const {
  addMessage,
  appendToStreamingMessage,
  removeTempMessage,
  clearChat,
  setStatusMessage,
  setPendingContentReferences
} = aiChatSlice.actions;
export default aiChatSlice.reducer;
